import { Slider } from "@frontend/components/ui/slider"
import type { ScoreSchema } from "@frontend/pages/case-workspace/radiology/types"
import type { ScoreAnnotationState } from "@shared/lib/history-types"
import { useState } from "react"

interface ScoreModulePanelProps {
  schema: ScoreSchema
  moduleId: string
  seriesId: string | null
  scores: ScoreAnnotationState[]
  onUpsertScore: (score: ScoreAnnotationState) => void
}

export function ScoreModulePanel({ schema, moduleId, seriesId, scores, onUpsertScore }: ScoreModulePanelProps) {
  const [generatedId] = useState(() => crypto.randomUUID())
  const existing = scores.find((s) => s.projectModuleId === moduleId && s.seriesId === seriesId && s.labelName === null)
  const currentId = existing?.id ?? generatedId

  function setValue(key: string, value: string | number | boolean | null) {
    onUpsertScore({
      id: currentId,
      seriesId,
      projectModuleId: moduleId,
      labelName: null,
      labelValue: null,
      values: { ...(existing?.values ?? {}), [key]: value }
    })
  }

  return (
    <div className="flex-1 overflow-auto p-3 space-y-4">
      {schema.fields.map((field) => {
        const currentVal = existing?.values[field.key] ?? null
        return (
          <div key={field.key}>
            <div className="text-[11px] font-medium mb-1.5">
              {field.label}
              {field.required && <span className="text-red-500 ml-0.5">*</span>}
            </div>
            {field.type === "categorical" && field.options ? (
              <div className="flex flex-wrap gap-1">
                {field.options.map((opt) => (
                  <button
                    key={String(opt.value)}
                    onClick={() => setValue(field.key, opt.value)}
                    className={`px-2.5 py-1 rounded-md border text-[11px] transition-colors ${
                      currentVal === opt.value
                        ? "border-primary/60 bg-primary/10"
                        : "border-border bg-surface-raised hover:bg-accent/40"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : field.type === "likert" ? (
              <div className="flex gap-1">
                {(field.options ? field.options.map((o) => o.value) : [1, 2, 3, 4, 5]).map((v) => (
                  <button
                    key={String(v)}
                    onClick={() => setValue(field.key, v)}
                    className={`flex-1 py-1 rounded-md border text-[11px] font-medium transition-colors ${
                      currentVal === v
                        ? "border-primary/60 bg-primary/10"
                        : "border-border bg-surface-raised hover:bg-accent/40"
                    }`}
                  >
                    {String(v)}
                  </button>
                ))}
              </div>
            ) : field.min !== undefined && field.max !== undefined ? (
              <div>
                <Slider
                  value={[typeof currentVal === "number" ? currentVal : field.min]}
                  onValueChange={(v) => {
                    const val = Array.isArray(v) ? v[0] : (v as number)
                    setValue(field.key, val !== undefined ? val : (field.min ?? 0))
                  }}
                  min={field.min}
                  max={field.max}
                  step={1}
                />
                <div className="mt-1 text-[10px] text-muted-foreground">
                  {typeof currentVal === "number" ? currentVal : "—"} / {field.max}
                </div>
              </div>
            ) : (
              <input
                type="number"
                value={typeof currentVal === "number" ? String(currentVal) : ""}
                onChange={(e) => setValue(field.key, e.target.value === "" ? null : Number(e.target.value))}
                className="w-full rounded-md border border-border bg-surface-raised px-2.5 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                placeholder="Enter value"
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
